import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PORTFOLIO_CATEGORIES = [
  "Todos",
  "Ciclismo de Estrada",
  "Ciclocross",
  "Mountain Bike",
  "Trail Running & Atletismo",
  "Off-Road & Todo-o-Terreno",
  "Retratos Artísticos",
  "Preto & Branco",
];

const slug = (s) => s.toLowerCase().replace(/&/g, "e").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const CategoryBar = ({ active, onChange }) => {
  const scrollerRef = useRef(null);
  const [hasLeft, setHasLeft] = useState(false);
  const [hasRight, setHasRight] = useState(false);

  const updateIndicators = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setHasLeft(el.scrollLeft > 8);
    setHasRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 24);
  }, []);

  useEffect(() => {
    updateIndicators();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateIndicators, { passive: true });
    window.addEventListener("resize", updateIndicators);
    return () => {
      el.removeEventListener("scroll", updateIndicators);
      window.removeEventListener("resize", updateIndicators);
    };
  }, [updateIndicators]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(240, el.clientWidth * 0.6);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="categorias"
      className="bg-black border-t border-white/10"
      data-testid="category-bar-section"
      aria-label="Filtro de categorias"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-8">
        <div className="flex items-center justify-between mb-5">
          <span className="font-heading text-[#c0392b] tracking-[0.4em] text-xs uppercase">
            Filtrar por Categoria
          </span>
          <span className="hidden md:inline font-heading text-white/40 tracking-[0.25em] text-[10px] uppercase">
            {active}
          </span>
        </div>

        <div className="relative">
          {/* Left arrow */}
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={!hasLeft}
            className={`absolute top-1/2 -translate-y-1/2 -left-2 md:-left-4 z-20 w-10 h-10 md:w-11 md:h-11 border bg-black flex items-center justify-center transition-all duration-200 ${
              hasLeft
                ? "border-white text-white hover:border-[#c0392b] hover:text-[#c0392b] cursor-pointer"
                : "border-white/20 text-white/20 cursor-not-allowed"
            }`}
            aria-label="Categorias anteriores"
            data-testid="category-arrow-left"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Right arrow */}
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={!hasRight}
            className={`absolute top-1/2 -translate-y-1/2 -right-2 md:-right-4 z-20 w-10 h-10 md:w-11 md:h-11 border bg-black flex items-center justify-center transition-all duration-200 ${
              hasRight
                ? "border-white text-white hover:border-[#c0392b] hover:text-[#c0392b] cursor-pointer"
                : "border-white/20 text-white/20 cursor-not-allowed"
            }`}
            aria-label="Categorias seguintes"
            data-testid="category-arrow-right"
          >
            <ChevronRight size={20} />
          </button>

          {/* Gradient edge fades */}
          {hasLeft && (
            <div
              className="pointer-events-none absolute inset-y-0 left-8 md:left-10 w-10 z-10"
              style={{ background: "linear-gradient(to right, #000 0%, rgba(0,0,0,0) 100%)" }}
              aria-hidden="true"
            />
          )}
          {hasRight && (
            <div
              className="pointer-events-none absolute inset-y-0 right-8 md:right-10 w-10 z-10"
              style={{ background: "linear-gradient(to left, #000 0%, rgba(0,0,0,0) 100%)" }}
              aria-hidden="true"
            />
          )}

          <div
            ref={scrollerRef}
            className="flex gap-3 overflow-x-auto pb-2 px-12 md:px-14"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            data-testid="category-scroller"
          >
            <style>{`[data-testid="category-scroller"]::-webkit-scrollbar{display:none}`}</style>
            {PORTFOLIO_CATEGORIES.map((cat) => {
              const isActive = cat === active;
              return (
                <button
                  key={cat}
                  onClick={() => onChange(cat)}
                  className={`shrink-0 font-heading text-xs md:text-sm tracking-[0.25em] uppercase px-5 md:px-7 py-3 border transition-colors duration-250 whitespace-nowrap ${
                    isActive
                      ? "bg-[#c0392b] text-white border-[#c0392b]"
                      : "bg-transparent text-white border-white hover:border-[#c0392b] hover:text-[#c0392b]"
                  }`}
                  data-testid={`category-btn-${slug(cat)}`}
                  aria-pressed={isActive}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryBar;
